'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import SearchModal from '@/components/SearchModal';
import { getFeaturedCaseStudies, getAllCaseStudies } from '@/lib/data/case-studies';
import { getAllServices } from '@/lib/data/services';
import { getAllIndustries } from '@/lib/data/industries';
import { Search, Home, Shield, Cloud, BookOpen, Briefcase, FileText } from 'lucide-react';
import type { BlogPost } from '@/lib/data/blog-d1';
interface SuggestedPage {
    title: string;
    href: string;
    description: string;
    icon: React.ReactNode;
    score?: number; // Relevance score for sorting
}
export default function NotFound() {
    const pathname = usePathname();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [suggestions, setSuggestions] = useState<SuggestedPage[]>([]);
    // not-found.tsx cannot export `metadata` — Next only reads it from page and
    // layout segments — and every route with `dynamicParams = false` (the whole
    // knowledge base) serves this page statically, so no generateMetadata() runs
    // for those URLs and the document ships with no <title>. That is an axe
    // `document-title` failure (WCAG 2.4.2). Set it here so the rendered 404
    // always has one.
    useEffect(() => {
        document.title = 'Page Not Found | Inventive HQ';
    }, []);
    // Track 404 errors to D1 database
    useEffect(() => {
        const track404 = async () => {
            try {
                await fetch('/api/404-tracking', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        requestedPath: pathname || window.location.pathname,
                        referrer: document.referrer || null,
                    }),
                });
            } catch (error) {
                // Silently fail - don't block user experience
                console.debug('Failed to track 404:', error);
            }
        };

        if (pathname) {
            track404();
        }
    }, [pathname]);

    useEffect(() => {
        // Helper function to calculate string similarity (simple version)
        const stringSimilarity = (str1: string, str2: string): number => {
            const longer = str1.length > str2.length ? str1 : str2;
            const shorter = str1.length > str2.length ? str2 : str1;
            if (longer.length === 0)
                return 1.0;
            const editDistance = (s1: string, s2: string): number => {
                s1 = s1.toLowerCase();
                s2 = s2.toLowerCase();
                const costs: number[] = [];
                for (let i = 0; i <= s1.length; i++) {
                    let lastValue = i;
                    for (let j = 0; j <= s2.length; j++) {
                        if (i === 0) {
                            costs[j] = j;
                        }
                        else if (j > 0) {
                            let newValue = costs[j - 1];
                            if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
                                newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
                            }
                            costs[j - 1] = lastValue;
                            lastValue = newValue;
                        }
                    }
                    if (i > 0)
                        costs[s2.length] = lastValue;
                }
                return costs[s2.length];
            };
            return (longer.length - editDistance(longer, shorter)) / longer.length;
        };

        const loadSuggestions = async () => {
            const suggestedPages: SuggestedPage[] = [];
            const path = pathname?.toLowerCase() || '';
            const slug = path.replace(/^\//, '').replace(/\/$/, '').split('/').pop() || '';

            // Fetch blog posts from API
            let allBlogPosts: BlogPost[] = [];
            let featuredBlogPosts: BlogPost[] = [];
            try {
                const [allResponse, featuredResponse] = await Promise.all([
                    fetch('/api/blog/posts?limit=100'),
                    fetch('/api/blog/posts?featured=true&limit=6')
                ]);
                if (allResponse.ok) {
                    const data = await allResponse.json();
                    allBlogPosts = data.posts || [];
                }
                if (featuredResponse.ok) {
                    const data = await featuredResponse.json();
                    featuredBlogPosts = data.posts || [];
                }
            } catch (error) {
                console.debug('Failed to fetch blog posts for 404 suggestions:', error);
            }

            // Try to find similar actual pages first
            const allServices = getAllServices();
            const allIndustries = getAllIndustries();
            const allCaseStudies = getAllCaseStudies();
        // Check for similar services
        allServices.forEach(service => {
            const similarity = stringSimilarity(slug, service.slug);
            if (similarity > 0.6 || path.includes(service.slug.substring(0, 5))) {
                suggestedPages.push({
                    title: service.name,
                    href: `/services/${service.slug}`,
                    description: service.description,
                    icon: <Shield className="w-6 h-6"/>,
                    score: similarity * 100
                });
            }
        });
        // Check for similar industries
        allIndustries.forEach(industry => {
            const similarity = stringSimilarity(slug, industry.slug);
            if (similarity > 0.6 || path.includes(industry.name.toLowerCase().split(' ')[0])) {
                suggestedPages.push({
                    title: industry.name,
                    href: `/${industry.slug}`,
                    description: industry.description,
                    icon: <Briefcase className="w-6 h-6"/>,
                    score: similarity * 100
                });
            }
        });
        // Check for similar blog posts
        allBlogPosts.slice(0, 50).forEach(post => {
            const similarity = stringSimilarity(slug, post.slug);
            if (similarity > 0.7) {
                suggestedPages.push({
                    title: post.title,
                    href: `/blog/${post.slug}`,
                    description: post.excerpt || post.description,
                    icon: <BookOpen className="w-6 h-6"/>,
                    score: similarity * 100
                });
            }
        });
        // Check for similar case studies
        allCaseStudies.forEach(caseStudy => {
            const similarity = stringSimilarity(slug, caseStudy.slug);
            if (similarity > 0.7) {
                suggestedPages.push({
                    title: caseStudy.title,
                    href: `/case-study/${caseStudy.slug}`,
                    description: caseStudy.description,
                    icon: <FileText className="w-6 h-6"/>,
                    score: similarity * 100
                });
            }
        });
        // Add category-specific suggestions based on keywords
        // Blog posts for blog URLs
        if (path.includes('blog') || path.includes('article') || path.includes('post')) {
            featuredBlogPosts.slice(0, 3).forEach(post => {
                suggestedPages.push({
                    title: post.title,
                    href: `/blog/${post.slug}`,
                    description: post.excerpt || post.description,
                    icon: <BookOpen className="w-6 h-6"/>,
                    score: 60
                });
            });
            suggestedPages.push({
                title: 'All Blog Posts',
                href: '/blog',
                description: 'Browse all our articles and insights',
                icon: <BookOpen className="w-6 h-6"/>,
                score: 50
            });
        }
        // Case studies for case study URLs
        if (path.includes('case-study') || path.includes('case-studies')) {
            const featuredCaseStudies = getFeaturedCaseStudies(3);
            featuredCaseStudies.forEach(caseStudy => {
                suggestedPages.push({
                    title: caseStudy.title,
                    href: `/case-study/${caseStudy.slug}`,
                    description: caseStudy.description,
                    icon: <FileText className="w-6 h-6"/>,
                    score: 60
                });
            });
            suggestedPages.push({
                title: 'All Case Studies',
                href: '/case-study',
                description: 'Browse all success stories',
                icon: <FileText className="w-6 h-6"/>,
                score: 50
            });
        }
        // Security-related content
        if (path.includes('security') || path.includes('cyber') || path.includes('threat') ||
            path.includes('mdr') || path.includes('edr') || path.includes('soc')) {
            suggestedPages.push({
                title: '24/7 Detection & Response',
                href: '/services/24-7-detection-and-response',
                description: 'Round-the-clock threat monitoring',
                icon: <Shield className="w-6 h-6"/>,
                score: 70
            });
            // Add security blog posts
            const securityPosts = allBlogPosts.filter(post => post.categories.some(cat => cat.toLowerCase().includes('security') || cat.toLowerCase().includes('mdr'))).slice(0, 2);
            securityPosts.forEach(post => {
                suggestedPages.push({
                    title: post.title,
                    href: `/blog/${post.slug}`,
                    description: post.excerpt || post.description,
                    icon: <BookOpen className="w-6 h-6"/>,
                    score: 55
                });
            });
        }
        // Cloud-related content
        if (path.includes('cloud') || path.includes('aws') || path.includes('gcp') || path.includes('azure')) {
            suggestedPages.push({
                title: 'Cloud Solutions',
                href: '/services/cloud-solutions',
                description: 'Cloud migration and optimization',
                icon: <Cloud className="w-6 h-6"/>,
                score: 70
            });
            // Add cloud blog posts
            const cloudPosts = allBlogPosts.filter(post => post.categories.some(cat => cat.toLowerCase().includes('cloud'))).slice(0, 2);
            cloudPosts.forEach(post => {
                suggestedPages.push({
                    title: post.title,
                    href: `/blog/${post.slug}`,
                    description: post.excerpt || post.description,
                    icon: <BookOpen className="w-6 h-6"/>,
                    score: 55
                });
            });
        }
        // Compliance content
        if (path.includes('compliance') || path.includes('hipaa') || path.includes('soc2') ||
            path.includes('gdpr') || path.includes('pci')) {
            suggestedPages.push({
                title: 'Compliance Services',
                href: '/services/compliance',
                description: 'Regulatory compliance solutions',
                icon: <Shield className="w-6 h-6"/>,
                score: 70
            });
        }
        // Industry-specific content
        const industry = path.includes('healthcare') ? 'Healthcare' :
            path.includes('finance') ? 'Finance' :
                path.includes('legal') ? 'Legal' : null;
        if (industry) {
            // Add industry-specific case studies
            const industryCaseStudies = allCaseStudies.filter(cs => cs.industry === industry).slice(0, 2);
            industryCaseStudies.forEach(cs => {
                suggestedPages.push({
                    title: cs.title,
                    href: `/case-study/${cs.slug}`,
                    description: cs.description,
                    icon: <FileText className="w-6 h-6"/>,
                    score: 65
                });
            });
        }
        // Fallback suggestions if nothing matched well
        if (suggestedPages.length === 0) {
            suggestedPages.push({
                title: 'Homepage',
                href: '/',
                description: 'Return to our homepage',
                icon: <Home className="w-6 h-6"/>,
                score: 30
            }, {
                title: 'Services',
                href: '/services',
                description: 'Browse all services',
                icon: <Shield className="w-6 h-6"/>,
                score: 25
            }, {
                title: 'Industries',
                href: '/industries',
                description: 'Industry-specific solutions',
                icon: <Briefcase className="w-6 h-6"/>,
                score: 20
            }, {
                title: 'Blog',
                href: '/blog',
                description: 'Latest insights',
                icon: <BookOpen className="w-6 h-6"/>,
                score: 15
            });
        }
            // Sort by score and remove duplicates
            const uniqueSuggestions = suggestedPages
                .sort((a, b) => (b.score || 0) - (a.score || 0))
                .filter((page, index, self) => index === self.findIndex(p => p.href === page.href))
                .slice(0, 6); // Get top 6
            setSuggestions(uniqueSuggestions);
        };

        loadSuggestions();
    }, [pathname]);
    return (<>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center px-4 py-16">
        <div className="max-w-4xl w-full text-center">
          {/* 404 Header */}
          <div className="mb-8">
            <h1 className="text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] mb-4" id="404">
              404
            </h1>
            <h2 className="text-3xl md:text-4xl font-bold text-[#4B4F58] mb-4" id="page-not-found">
              Page Not Found
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              The page you're looking for doesn't exist or has been moved.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-12">
            <button onClick={() => setIsSearchOpen(true)} className="mx-auto flex items-center gap-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-[#2813e8] rounded-lg px-6 py-4 text-left transition-all duration-200 shadow-lg hover:shadow-xl max-w-2xl w-full">
              <Search className="w-5 h-5 text-gray-400"/>
              <span className="text-gray-500 dark:text-gray-400 flex-1">Search our site...</span>
              <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded">
                ⌘K
              </kbd>
            </button>
          </div>

          {/* Suggested Pages */}
          {suggestions.length > 0 && (<div className="mb-12">
              <h3 className="text-2xl font-bold text-[#4B4F58] mb-6" id="were-you-looking-for-one-of-these">
                Were you looking for one of these?
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {suggestions.map((page, index) => (<Link key={index} href={page.href} className="group bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-[#2813e8] rounded-lg p-6 text-left transition-all duration-200 hover:shadow-lg">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 text-[#2813e8] group-hover:scale-110 transition-transform">
                        {page.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-semibold text-[#4B4F58] group-hover:text-[#2813e8] transition-colors mb-1 line-clamp-2">
                          {page.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{page.description}</p>
                      </div>
                    </div>
                  </Link>))}
              </div>
            </div>)}

          {/* Quick Links */}
          <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-8">
            <p className="text-gray-600 dark:text-gray-400 mb-4">Or explore these popular pages:</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link href="/" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white rounded-md font-semibold hover:shadow-lg transition-shadow">
                <Home className="w-4 h-4"/>
                Go Home
              </Link>
              <Link href="/services" className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 border-2 border-[#2813e8] text-[#2813e8] rounded-md font-semibold hover:bg-[#2813e8] hover:text-white transition-colors">
                <Shield className="w-4 h-4"/>
                Services
              </Link>
              <Link href="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-[#4B4F58] rounded-md font-semibold hover:border-[#2813e8] hover:text-[#2813e8] transition-colors">
                Contact Us
              </Link>
            </div>
          </div>

          {/* Attempted URL Display (for debugging/transparency) */}
          {pathname && (<div className="mt-8 text-sm text-gray-400">
              <p>Attempted URL: <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{pathname}</code></p>
            </div>)}
        </div>
      </div>

      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)}/>
    </>);
}
